import { Injectable, signal, inject, computed } from '@angular/core';
import { AuthService } from './auth.service';
import { PermissionsService } from './permissions.service';
import { Router } from '@angular/router';
import { ChatService } from './chat.service';

/**
 * Defines the structure for a navigation item.
 */
export interface NavItem {
  label: string;
  icon: string; // Using lucide-angular icon names
  route?: string | any[];
  handler?: () => void;
  children?: NavItem[]; // For future dropdowns/sub-menus
  badge?: string | number; // Optional badge for notifications
  section?: string; // Optional domain group header (e.g. 'Operations', 'Finance', 'Administration')
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private authService = inject(AuthService);
  private perms = inject(PermissionsService);
  private router = inject(Router);
  private chat = inject(ChatService);

  /**
   * Primary navigation items — reactive based on permissions and grouped by domain.
   */
  readonly topNavItems = computed<NavItem[]>(() => {
    const items: NavItem[] = [
      // Operations Domain
      {
        label: 'Dashboard',
        icon: 'layout-grid',
        route: '/dashboard',
        section: 'Operations',
      },
      {
        label: 'Loads',
        icon: 'truck',
        route: '/loads',
      },
      {
        label: 'Fleet',
        icon: 'car',
        route: '/fleet',
      },
      {
        label: 'Drivers',
        icon: 'id-card',
        route: '/drivers',
      },
      // S4: Driver ↔ dispatch chat with live unread badge.
      {
        label: 'Messages',
        icon: 'message-square',
        route: '/messages',
        badge: this.chat.totalUnread() > 99 ? '99+' : this.chat.totalUnread() || undefined,
      },
    ];

    // Finance Domain — visible to root/admin/accounting users
    if (this.perms.canViewBilling()) {
      items.push(
        {
          label: 'Invoicing',
          icon: 'file-text',
          route: '/invoicing',
          section: 'Finance',
        },
        {
          label: 'Payroll',
          icon: 'wallet',
          route: '/payroll',
        },
      );
    } else if (this.perms.canViewPayroll()) {
      // S2: dispatch managers get read-only payroll visibility.
      items.push({
        label: 'Payroll',
        icon: 'wallet',
        route: '/payroll',
        section: 'Finance',
      });
    }

    // Administration Domain
    items.push(
      {
        label: 'Partners',
        icon: 'users',
        route: '/partners',
        section: 'Administration',
      },
      {
        label: 'Team',
        icon: 'user-cog',
        route: '/team',
      },
    );

    return items;
  });

  /**
   * Holds the secondary action items for the bottom section of the sidebar.
   */
  readonly bottomNavItems = signal<NavItem[]>([
    {
      label: 'Settings',
      icon: 'settings',
      route: '/settings',
    },
    {
      label: 'Log Out',
      icon: 'log-out',
      handler: () => this.logOut(),
    },
  ]);

  constructor() {}

  private async logOut(): Promise<void> {
    await this.authService.logout();
    window.location.href = '/auth/login';
  }
}
